import { Batch } from '../../order/models/batch';
import { OrderItem } from '../../order/models/order-item';
import { ShipToDetails } from '../../order/models/ship-to-details';
import { Dimension } from '../../shared/models/dimension';
import { MillSelectorMill } from '../components/mill/mill-selector.component';

export interface Ecalls {
	id: number;
	type?: string;
	personId?: 0;
	visibilityGroupId?: 0;
	customerReference: string;
	rddMode?: string;
	copyTo?: string;
	comment?: string;
	rowCreationDate: string;
	rowCreationBy?: 0;
	ecallBatches?: CalledBatch[];
	ecallVolumes?: CalledVolume[];
	isSent?: 0;
	quantitySum?: number;
	millDescription?: string;
	callCreator?: string;
}

export interface EcallOffCardItemResponse {
	supplierCallOffItemNumber: string;
	supplierCallOffNumber: string;
	supplierBatchReference?: string;
	batchReference?: string;
	batchCancellationTimestamp?: string;
	batchIsCancelled?: number;
	supplierOrderNumber?: string;
	supplierOrderItemNumber?: string;
	requestedVolume?: number;
	requestedVolumeUnit?: string;
	mill?: string;
	millDescription?: string;
	callOffShipTo?: string;
	callOffShipToDescription?: string;
	soldTo?: string;
	soldToDescription?: string;
	shipTo?: string;
	shipToDescription?: string;
	requestedDeliveryDateFrom?: string;
	requestedDeliveryDateTo?: string;
	unloadingReference?: string;
	comment?: string;
	callOffItemCreationTimestamp?: string;
	qtyBatchNet?: number;
	qtyBatchBrut?: number;
	batchStatus?: string;
	batchStatusDescription?: string;
	customerOrderNumber?: string;
	customerOrderItemNumber?: string;
	customerArticleReference?: string;
	callOffTechnicalReference?: string;
}

export interface EcallOffCardResponse {
	supplierCallOffNumber: string;
	callOffType?: string;
	customerCallOffReference?: string;
	copyTo?: string;
	comment?: string;
	callOffCreationTimestamp?: string;
	callOffCreationBy?: number;
	callOffCreatorId?: number;
	callOffCreator?: string;
	callOffOrigin?: string;
	requestedDateForCallOffType?: string;
	soldToConcat?: string;
	shipToConcat?: string;
	items: EcallOffCardItemResponse[];
	quantitySum?: number;
}

export type FieldOfEcalls = keyof Ecalls
	| `ecallBatches.${ keyof CalledBatch }`
	| `ecallVolumes.${ keyof CalledVolume }`;

export type FieldOfEcallDatacenter = keyof EcallDatacenter;
export interface EcallResponse {
	id?: number;
	type: string;
	customerReference?: string;
	millCode?: string;
	copyTo?: string;
	comment?: string;
	rddMode?: string;
	ecallBatches?: CalledBatch[];
	ecallItems?: CalledVolume[];
}

export interface CalledBatch {
	id?: number;
	ecallMillBatchReference?: string;
	batchReference: string;
	cancellationDate?: string;
	comment?: string;
	grossWeight?: number;
	millCode: string;
	millDescription: string;
	netWeight?: number;
	requestedDeliveryDateFrom?: string;
	requestedDeliveryDateTo?: string;
	rowCreationBy?: number;
	rowCreationDate?: string;
	sapBatchId: string;
	unloadingReference?: string;
	vendorBatchId?: string;
	shipTo?: string;
	shipToDescription?: string;
	originalShipTo?: string;
	originalShipToDescription?: string;
	soldTo?: string;
	soldToDescription?: string;
	customerOrderReference: string;
	customerOrderItem: string;
	batchStatus?: string;
	supplierOrderItem: string;
	supplierOrderReference: string;
	supplierBatchReference?: string;
	customerArticleReference: string;
	statusDescription: string;
	batchData: Batch[];
	allowChangeOfShipto: number;
}

export interface GroupCalledBatch {
	millCodeGroup: CalledBatch[];
	millDescription: string;
	grossWeight: number;
	netWeight: number;
}
export interface GroupCalledVolume {
	millCode: string;
	millDescription?: string;
	millCodeGroup: CalledVolume[];
	requestedVolume: number;
}

export interface CalledVolume extends Dimension {
	id?: number;
	ecallMillVolumeReference?: string;
	comment?: string;
	customerOrderItem?: string;
	customerOrderReference?: string;
	millCode: string;
	millDescription?: string;
	requestedDeliveryDateFrom?: string;
	requestedDeliveryDateTo?: string;
	requestedVolume: number;
	requestedVolumeUnit: string;
	steelgrade?: string;
	supplierOrderItem: string;
	supplierOrderReference: string;
	unloadingReference?: string;
	shipTo?: string;
	shipToDescription?: string;
	originalShipTo?: string;
	originalShipToDescription?: string;
	customerArticleReference?: string;
}

export type FieldOfCalledBatch = keyof CalledBatch;

export const RDD_MODE = ['ASAP', 'BETWEEN', 'EARLIEST', 'LATEST'];

export abstract class EcallOffPageContent {
	abstract checkErrors(): boolean | undefined;
}

export interface CallableOrderItem extends OrderItem {
	requestedVolume?: number;
	calledWeight?: number;
	comment?: string;
	requestedDeliveryDateFrom?: Date;
	requestedDeliveryDateTo?: Date;
	unloadingReference?: string;
	shipTo?: ShipToDetails;
	serviceLevel?: string;
	serviceLevelDescription?: string;
	originalShipTo?: ShipToDetails;
	originalShipToDescription?: string;
}

export interface EcallOffByVolumeStepState extends Record<string, unknown> {
	copyTo?: string;
	mills?: MillSelectorMill[];
	customerCalloffReference?: string;
	rddMode?: string;
	selectedOrderItems?: CallableOrderItem[];
}

export interface EcallDatacenter {
	supplierCallOffNumber: string;
	callOffType?: string;
	customerCallOffReference: string;
	comment?: string;
	callOffCreationTimestamp: string;
	callOffCreationBy?: number;
	callOffCreatorId?: number;
	callOffCreator?: string;
	callOffOrigin?: string;
	requestedDateForCallOffType?: string;
	copyTo?: string;
	supplierCallOffItemNumberConcat?: string;
	supplierBatchReferenceConcat?: string;
	batchReferenceConcat?: string;
	batchCancellationTimestampConcat?: string;
	batchIsCancelledConcat?: string;
	supplierOrderNumberConcat?: string;
	supplierOrderItemNumberConcat?: string;
	requestedVolumeConcat?: string;
	requestedVolumeUnitConcat?: string;
	millConcat?: string;
	millDescriptionConcat?: string;
	millRequestIdConcat?: string;
	millFeedbackAcknowledgedConcat?: string;
	callOffShipToConcat?: string;
	callOffShipToDescriptionConcat?: string;
	soldToConcat?: string;
	soldToDescriptionConcat?: string;
	shipToConcat?: string;
	shipToDescriptionConcat?: string;
	requestedDeliveryDateFromConcat?: string;
	requestedDeliveryDateToConcat?: string;
	unloadingReferenceConcat?: string;
	commentConcat?: string;
	callOffItemCreationTimestampConcat?: string;
	callOffTechnicalReferenceConcat?: string;
	millDescription?: string;
	quantitySum?: number;
}
